/**
 * Bike Configuration Service
 * Handles API calls for bike configurations
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get all bike configurations for the current user
 */
export const getAllBikeConfigs = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/bike-configs/`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch bike configurations: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Get a specific bike configuration by type
 * Returns 404 if not found
 */
export const getBikeConfig = async (bikeType) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/bike-configs/${bikeType}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 404) {
    return null; // Not configured yet
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch bike configuration: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Get a bike configuration with defaults
 * Always returns a configuration (saved or defaults)
 */
export const getBikeConfigWithDefaults = async (bikeType) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/bike-configs/${bikeType}/with-defaults`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch bike configuration: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Get default configuration for a bike type (no auth required)
 */
export const getDefaultConfig = async (bikeType) => {
  const response = await fetch(`${API_BASE_URL}/bike-configs/${bikeType}/defaults`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch default configuration: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Create or update a bike configuration (auto-save)
 */
export const saveBikeConfig = async (configData) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/bike-configs/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(configData),
  });

  if (!response.ok) {
    throw new Error(`Failed to save bike configuration: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Partially update a bike configuration
 */
export const updateBikeConfig = async (bikeType, updates) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/bike-configs/${bikeType}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update bike configuration: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Delete a bike configuration
 */
export const deleteBikeConfig = async (bikeType) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/bike-configs/${bikeType}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete bike configuration: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Get human-readable bike type label
 */
export const getBikeTypeLabel = (bikeType) => {
  const labels = {
    'road': 'Road Bike',
    'road_aero': 'Road Bike with Aerobars',
    'tt': 'Time Trial Bike',
    'gravel': 'Gravel Bike',
  };
  return labels[bikeType] || bikeType;
};

/**
 * Get field labels for a bike type
 */
export const getFieldLabels = (bikeType) => {
  const commonFields = {
    bike_weight: 'Bike Weight',
  };

  const typeSpecificFields = {
    'road': {
      road_cda_hoods: 'CdA (Hoods)',
      road_cda_drops: 'CdA (Drops)',
      road_drops_min_speed: 'Min Speed for Drops',
    },
    'road_aero': {
      road_aero_cda_hoods: 'CdA (Hoods)',
      road_aero_cda_aerobar: 'CdA (Aerobar)',
      road_aero_aerobar_min_speed: 'Min Speed for Aerobar',
      road_aero_aerobar_max_speed: 'Max Speed for Aerobar',
      road_aero_cda_drops: 'CdA (Drops)',
      road_aero_drops_min_speed: 'Min Speed for Drops',
    },
    'tt': {
      tt_cda_base: 'CdA (Base)',
      tt_cda_aero: 'CdA (Aero)',
      tt_aero_min_speed: 'Min Speed for Aero',
      tt_aero_max_speed: 'Max Speed for Aero',
    },
    'gravel': {
      gravel_cda_hoods: 'CdA (Hoods)',
      gravel_cda_drops: 'CdA (Drops)',
      gravel_drops_min_speed: 'Min Speed for Drops',
    },
  };

  return {
    ...commonFields,
    ...(typeSpecificFields[bikeType] || {}),
  };
};
